import { useEffect, useRef, useCallback, useState } from 'react'
import type { HierarchicalReferral } from '@/models/referral'

interface OrbitalTreeNode {
  id: string
  name: string
  initial: string
  level: number
  hasSubscription: boolean
  color: string
  x: number
  y: number
  radius: number
  isCenter: boolean
  orbitIndex: number
  data: HierarchicalReferral
}

interface OrbitalTreeProps {
  referrals: HierarchicalReferral[]
  currentUserName: string
  onNodeClick: (referral: HierarchicalReferral) => void
  currentCenterId?: string
}

const PROJECT_COLORS = [
  '#0dcbfc',
  '#22d3ee',
  '#67e8f9',
  '#06b6d4',
  '#0891b2',
  '#155e75',
  '#164e63',
]

const MIN_ZOOM = 0.5
const MAX_ZOOM = 2.0
const ZOOM_STEP = 0.25

function getProjectColor(seed: string): string {
  let hash = 0
  for (let i = 0; i < seed.length; i++) {
    const char = seed.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash
  }
  const index = Math.abs(hash) % PROJECT_COLORS.length
  return PROJECT_COLORS[index]
}

function getInitials(name: string): string {
  return name.charAt(0).toUpperCase()
}

function calculateOrbits(referrals: HierarchicalReferral[]): Map<number, HierarchicalReferral[]> {
  const orbits = new Map<number, HierarchicalReferral[]>()
  let orbitIndex = 0
  let orbitLimit = 7
  let currentIndex = 0

  while (currentIndex < referrals.length) {
    const orbitNodes = referrals.slice(currentIndex, currentIndex + orbitLimit)
    if (orbitNodes.length > 0) {
      orbits.set(orbitIndex, orbitNodes)
    }
    currentIndex += orbitLimit
    orbitIndex++
    orbitLimit += 3
  }

  return orbits
}

export function OrbitalTree({ referrals, currentUserName, onNodeClick, currentCenterId }: OrbitalTreeProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const nodesRef = useRef<OrbitalTreeNode[]>([])
  const orbitsRef = useRef<Map<number, HierarchicalReferral[]>>(new Map())
  const [hoveredNode, setHoveredNode] = useState<string | null>(null)
  const [zoom, setZoom] = useState(1)
  const animationRef = useRef<number | undefined>(undefined)
  const rotationRef = useRef(0)
  const dprRef = useRef(1)
  const transitionRef = useRef({
    isTransitioning: false,
    progress: 0,
    duration: 500,
    startTime: 0,
    oldNodes: [] as OrbitalTreeNode[],
    newNodes: [] as OrbitalTreeNode[]
  })

  const handleZoomIn = useCallback(() => {
    setZoom(prev => Math.min(prev + ZOOM_STEP, MAX_ZOOM))
  }, [])

  const handleZoomOut = useCallback(() => {
    setZoom(prev => Math.max(prev - ZOOM_STEP, MIN_ZOOM))
  }, [])

  const calculateNodes = useCallback((): OrbitalTreeNode[] => {
    const canvas = canvasRef.current
    if (!canvas) return []

    const displayWidth = canvas.clientWidth
    const displayHeight = canvas.clientHeight

    const centerX = displayWidth / 2
    const centerY = displayHeight / 2
    const centerRadius = 32
    const baseOrbitRadius = Math.min(displayWidth, displayHeight) * 0.25
    const baseNodeRadius = 22

    const nodes: OrbitalTreeNode[] = []

    nodes.push({
      id: currentCenterId || 'center',
      name: currentUserName,
      initial: getInitials(currentUserName),
      level: 0,
      hasSubscription: true,
      color: '#0dcbfc',
      x: centerX,
      y: centerY,
      radius: centerRadius,
      isCenter: true,
      orbitIndex: -1,
      data: { user: currentUserName, created_at: '', level: 0, subscriptions: [], children: referrals }
    })

    const orbits = calculateOrbits(referrals)
    orbitsRef.current = orbits

    orbits.forEach((orbitReferrals, orbitIndex) => {
      const orbitRadius = baseOrbitRadius * (1 + orbitIndex * 0.5)
      const nodeRadius = Math.max(baseNodeRadius - orbitIndex * 2, 14)
      const angleStep = (2 * Math.PI) / Math.max(orbitReferrals.length, 1)

      orbitReferrals.forEach((referral, indexInOrbit) => {
        const angle = angleStep * indexInOrbit - Math.PI / 2
        const x = centerX + Math.cos(angle) * orbitRadius
        const y = centerY + Math.sin(angle) * orbitRadius

        nodes.push({
          id: `${referral.user}-${orbitIndex}-${indexInOrbit}`,
          name: referral.user,
          initial: getInitials(referral.user),
          level: 1,
          hasSubscription: referral.subscriptions && referral.subscriptions.length > 0,
          color: getProjectColor(referral.user),
          x,
          y,
          radius: nodeRadius,
          isCenter: false,
          orbitIndex,
          data: referral
        })
      })
    })

    return nodes
  }, [referrals, currentUserName, currentCenterId])

  const drawNode = useCallback((ctx: CanvasRenderingContext2D, node: OrbitalTreeNode, opacity: number = 1, isHovered: boolean = false) => {

    ctx.save()
    ctx.globalAlpha = opacity

    if (isHovered) {
      ctx.beginPath()
      ctx.arc(node.x, node.y, node.radius + 8, 0, Math.PI * 2)
      ctx.fillStyle = 'rgba(13, 203, 252, 0.3)'
      ctx.fill()
    }

    ctx.beginPath()
    ctx.arc(node.x, node.y, node.radius, 0, Math.PI * 2)
    ctx.fillStyle = node.color
    ctx.fill()

    ctx.beginPath()
    ctx.arc(node.x, node.y, node.radius, 0, Math.PI * 2)
    ctx.strokeStyle = isHovered ? '#ffffff' : 'rgba(255, 255, 255, 0.3)'
    ctx.lineWidth = isHovered ? 3 : 2
    ctx.stroke()

    ctx.font = `bold ${node.isCenter ? '24px' : `${Math.max(14, node.radius - 4)}px`} sans-serif`
    ctx.fillStyle = '#ffffff'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(node.initial, node.x, node.y)

    const dotOffset = node.radius * 0.65
    const dotX = node.x + dotOffset
    const dotY = node.y - dotOffset
    const dotRadius = Math.max(3, node.radius * 0.18)

    ctx.beginPath()
    ctx.arc(dotX, dotY, dotRadius, 0, Math.PI * 2)
    ctx.fillStyle = node.hasSubscription ? '#22c55e' : '#ef4444'
    ctx.fill()

    ctx.beginPath()
    ctx.arc(dotX, dotY, dotRadius, 0, Math.PI * 2)
    ctx.strokeStyle = '#080c15'
    ctx.lineWidth = 1.5
    ctx.stroke()

    ctx.restore()
  }, [])

  const draw = useCallback(() => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx) return

    ctx.clearRect(0, 0, canvas.width, canvas.height)

    const transition = transitionRef.current

    if (transition.isTransitioning) {
      const elapsed = Date.now() - transition.startTime
      const progress = Math.min(elapsed / transition.duration, 1)

      if (progress < 0.5) {
        const fadeOutProgress = 1 - (progress * 2)
        transition.oldNodes.forEach(node => {
          drawNode(ctx, node, fadeOutProgress, false)
        })
      }

      if (progress > 0.3) {
        const fadeInProgress = (progress - 0.3) / 0.7
        transition.newNodes.forEach(node => {
          drawNode(ctx, node, fadeInProgress, false)
        })
      }

      if (progress >= 1) {
        transition.isTransitioning = false
        nodesRef.current = transition.newNodes
      }

      return
    }

    const nodes = nodesRef.current
    const centerNode = nodes.find(n => n.isCenter)
    const orbitalNodes = nodes.filter(n => !n.isCenter)

    const displayWidth = canvas.clientWidth
    const displayHeight = canvas.clientHeight
    const baseOrbitRadius = Math.min(displayWidth, displayHeight) * 0.25

    const orbits = orbitsRef.current
    const orbitCount = orbits.size

    if (centerNode && orbitCount > 0) {
      for (let orbitIndex = 0; orbitIndex < orbitCount; orbitIndex++) {
        const orbitRadius = baseOrbitRadius * (1 + orbitIndex * 0.5)

        const gradient = ctx.createRadialGradient(
          centerNode.x, centerNode.y, orbitRadius - 15,
          centerNode.x, centerNode.y, orbitRadius + 15
        )
        gradient.addColorStop(0, 'rgba(13, 203, 252, 0)')
        gradient.addColorStop(0.5, `rgba(13, 203, 252, ${0.08 - orbitIndex * 0.01})`)
        gradient.addColorStop(1, 'rgba(13, 203, 252, 0)')

        ctx.beginPath()
        ctx.arc(centerNode.x, centerNode.y, orbitRadius, 0, Math.PI * 2)
        ctx.strokeStyle = gradient
        ctx.lineWidth = 2
        ctx.stroke()

        ctx.beginPath()
        ctx.arc(centerNode.x, centerNode.y, orbitRadius, 0, Math.PI * 2)
        ctx.strokeStyle = `rgba(13, 203, 252, ${0.25 - orbitIndex * 0.03})`
        ctx.lineWidth = 1
        ctx.setLineDash([5, 5])
        ctx.stroke()
        ctx.setLineDash([])

        const dotCount = 3 + orbitIndex
        for (let i = 0; i < dotCount; i++) {
          const angle = rotationRef.current * (1 - orbitIndex * 0.1) + (i * (Math.PI * 2 / dotCount))
          const dotX = centerNode.x + Math.cos(angle) * orbitRadius
          const dotY = centerNode.y + Math.sin(angle) * orbitRadius

          ctx.beginPath()
          ctx.arc(dotX, dotY, 2, 0, Math.PI * 2)
          ctx.fillStyle = `rgba(13, 203, 252, ${0.6 - orbitIndex * 0.08})`
          ctx.fill()
        }
      }
    }

    if (centerNode) {
      orbitalNodes.forEach(node => {
        ctx.beginPath()
        ctx.moveTo(centerNode.x, centerNode.y)
        ctx.lineTo(node.x, node.y)
        ctx.strokeStyle = `rgba(13, 203, 252, ${0.15 - node.orbitIndex * 0.02})`
        ctx.lineWidth = 1
        ctx.stroke()
      })
    }

    nodes.forEach(node => {
      drawNode(ctx, node, 1, hoveredNode === node.id)
    })
  }, [hoveredNode, drawNode])

  const animate = useCallback(() => {
    rotationRef.current += 0.005
    draw()
    animationRef.current = requestAnimationFrame(animate)
  }, [draw])

  useEffect(() => {
    const canvas = canvasRef.current
    const container = containerRef.current
    if (!canvas || !container) return

    const resizeCanvas = () => {
      const rect = container.getBoundingClientRect()
      const dpr = window.devicePixelRatio || 1
      dprRef.current = dpr

      canvas.width = rect.width * dpr * zoom
      canvas.height = rect.height * dpr * zoom

      const ctx = canvas.getContext('2d')
      if (ctx) {
        ctx.scale(dpr * zoom, dpr * zoom)
      }

      canvas.style.width = `${rect.width}px`
      canvas.style.height = `${rect.height}px`

      nodesRef.current = calculateNodes()
    }

    resizeCanvas()
    window.addEventListener('resize', resizeCanvas)

    animationRef.current = requestAnimationFrame(animate)

    return () => {
      window.removeEventListener('resize', resizeCanvas)
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [calculateNodes, animate, zoom])

  useEffect(() => {
    const newNodes = calculateNodes()

    if (nodesRef.current.length > 0 && !transitionRef.current.isTransitioning) {
      transitionRef.current = {
        isTransitioning: true,
        progress: 0,
        duration: 500,
        startTime: Date.now(),
        oldNodes: [...nodesRef.current],
        newNodes: newNodes
      }
    } else {
      nodesRef.current = newNodes
    }
  }, [calculateNodes])

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current
    if (!canvas) return

    const rect = canvas.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top

    const hovered = nodesRef.current.find(node => {
      const dx = x - node.x
      const dy = y - node.y
      return Math.sqrt(dx * dx + dy * dy) <= node.radius
    })

    setHoveredNode(hovered?.id || null)
    canvas.style.cursor = hovered ? 'pointer' : 'default'
  }, [])

  const handleClick = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current
    if (!canvas) return

    const rect = canvas.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top

    const clicked = nodesRef.current.find(node => {
      const dx = x - node.x
      const dy = y - node.y
      return Math.sqrt(dx * dx + dy * dy) <= node.radius
    })

    if (clicked && !clicked.isCenter && !transitionRef.current.isTransitioning) {
      onNodeClick(clicked.data)
    }
  }, [onNodeClick])

  return (
    <div ref={containerRef} className="w-full h-[500px] relative">
      <canvas
        ref={canvasRef}
        className="w-full h-full"
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHoveredNode(null)}
        onClick={handleClick}
      />
      <div className="absolute bottom-4 right-4 flex flex-col gap-2">
        <button
          onClick={handleZoomIn}
          disabled={zoom >= MAX_ZOOM}
          className="w-10 h-10 rounded-lg bg-primary-800/80 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white flex items-center justify-center transition-colors backdrop-blur-sm border border-primary-600/30"
          aria-label="Zoom in"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
        </button>
        <button
          onClick={handleZoomOut}
          disabled={zoom <= MIN_ZOOM}
          className="w-10 h-10 rounded-lg bg-primary-800/80 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white flex items-center justify-center transition-colors backdrop-blur-sm border border-primary-600/30"
          aria-label="Zoom out"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
        </button>
      </div>
      <div className="absolute bottom-4 left-4 text-xs text-primary-400/70 font-medium">
        {Math.round(zoom * 100)}%
      </div>
    </div>
  )
}
